/**
 * Money & date formatting for the payments domain.
 * Money rule (handoff TASK-009): the API returns INTEGER TZS CENTS; every
 * display goes through formatTzs — never render raw cents.
 */

/**
 * "TZS 50,000" from integer cents (5,000,000 → TZS 50,000).
 * Sub-1-TZS amounts are shown in cents ("TZS 0.06") instead of rounding to
 * "TZS 0" — rounding reads like data loss to an agent (QC TASK-QC-002 F6).
 * Normal amounts are unchanged.
 */
export function formatTzs(cents: number): string {
  if (cents !== 0 && Math.abs(cents) < 100) {
    return `TZS ${(cents / 100).toFixed(2)}`;
  }
  const tzs = Math.round(cents / 100);
  return `TZS ${new Intl.NumberFormat("en-TZ", { maximumFractionDigits: 0 }).format(tzs)}`;
}

/** Bare grouped figure ("50,000") for amount-input previews. */
export function formatTzsFigure(cents: number): string {
  if (cents !== 0 && Math.abs(cents) < 100) {
    return (cents / 100).toFixed(2);
  }
  const tzs = Math.round(cents / 100);
  return new Intl.NumberFormat("en-TZ", { maximumFractionDigits: 0 }).format(tzs);
}

/** Parse a user-typed TZS amount ("50,000" / "50000") into integer cents. */
export function parseTzsToCents(raw: string): number | null {
  const digits = raw.replace(/[^\d]/g, "");
  if (!digits) return null;
  const tzs = Number(digits);
  if (!Number.isFinite(tzs) || tzs <= 0) return null;
  return tzs * 100;
}

/** "12 Aug 2026" from an ISO date/timestamp. */
export function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso.endsWith("Z") || iso.includes("+") || iso.length <= 10 ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

/** "5 Aug 2026, 11:34" from an ISO timestamp. */
export function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso.endsWith("Z") || iso.includes("+") ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return iso;
  const date = d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  return `${date}, ${time}`;
}

/** "11:34" from an ISO timestamp. */
export function formatTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso.endsWith("Z") || iso.includes("+") ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

/** Compact age since an ISO timestamp: "12 min" / "2 h" / "1 d". */
export function formatAge(iso: string): string {
  const d = new Date(iso.endsWith("Z") || iso.includes("+") ? iso : `${iso}Z`);
  const ms = Date.now() - d.getTime();
  if (!Number.isFinite(ms) || ms < 0) return "—";
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${Math.max(mins, 1)} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} h`;
  return `${Math.floor(hours / 24)} d`;
}

/** OpenPAYGO grouped display: "582940117366208" → "582 940 117 366 208". */
export function groupTokenDigits(token: string): string {
  const digits = token.replace(/\s+/g, "");
  return digits.replace(/(\d{3})(?=\d)/g, "$1 ");
}
